import { FundOpportunityEOIController } from './controller';
import { FundOpportunityState } from './types';

export function loadStaticPdfAvailability(ctx: FundOpportunityEOIController): void {
  if (ctx.props.print || ctx.props.printDraft) {
    return;
  }
  const id = ctx.state.fundOpportunity?.id;
  if (!id) {
    return;
  }
  ctx
    .getBackendService()
    .checkFundOpportunityPDFAvailable(id)
    .then((available) => {
      ctx.setState({ staticPdfAvailable: available });
    })
    .catch(() => {
      ctx.setState({ staticPdfAvailable: false });
    });
}

export function refreshInvestorPreservingScroll(ctx: FundOpportunityEOIController) {
  if (!ctx.props.investorContext || !ctx.state.investor?.id) {
    return;
  }

  if (typeof window !== 'undefined') {
    const key = getScrollStorageKey(ctx);
    if (key) {
      const payload = JSON.stringify({ ts: Date.now() });
      try {
        window.localStorage.setItem(key, payload);
      } catch (err) {
        console.warn('Unable to store EOI scroll flag', err);
      }
    }
  }

  ctx.props.investorContext.refreshInvestor(ctx.state.investor.id);
}

export function getScrollStorageKey(ctx: FundOpportunityEOIController): string | null {
  const investorId = ctx.state.investor?.id;
  if (!investorId) {
    return null;
  }
  return `eoi-section-scroll-${investorId}`;
}

export function resetModalFlags(_ctx: FundOpportunityEOIController): Partial<FundOpportunityState> {
  return {
    requestMessage: false,
    requestShare: false,
    requestAppointment: false,
    sendMessageResult: undefined,
    sendMessageError: false,
  };
}

export function hasSubmittedEOI(ctx: FundOpportunityEOIController): boolean {
  return !!ctx.state.investor?.indicativeInvestment;
}

export function setFilter(ctx: FundOpportunityEOIController, filter: string): void {
  ctx.setState({ filter });
}

export function generateHTML(ctx: FundOpportunityEOIController) {
  new Promise<void>((resolve) => setTimeout(resolve, 100)).then(() => {
    // find current css style for .hide-print and see if it's display: none
    const hidePrint = document.querySelector('.hide-print');
    const hidePrintStyle = window.getComputedStyle(hidePrint as Element);
    const display = hidePrintStyle.getPropertyValue('display');

    if (display === 'none') {
      document.documentElement.style.setProperty('--html-font-size', '12px');

      (ctx.getBackendService() as any)
        .uploadPDFHTML(
          document.querySelector('html')?.outerHTML as string,
          'FundOpportunity',
          ctx.state.fundOpportunity!.id,
          !!ctx.state.pdfDraft,
        )
        .then(() => {
          const printComplete = () => {
            window.parent.postMessage('print-complete');
          };
          if (ctx.state.pdfDraft) {
            ctx.downloadPDF(true, printComplete);
          } else {
            printComplete();
          }
        });
    } else {
      ctx.generateHTML();
    }
  });
}

export function currentStatus(ctx: FundOpportunityEOIController) {
  return ctx.state.fundOpportunity!.status;
}

export function downloadApplication(ctx: FundOpportunityEOIController) {
  ctx.setState({ loadingApplication: true });
  ctx
    .getBackendService()
    .getApplicationURL('FundOpportunity', ctx.state.fundOpportunity!.id)
    .then((url: string) => {
      const element = document.createElement('a');
      element.href = url;
      element.target = '_blank';
      element.download = '240709 Investment Memorandum - Wantirna Initial Class.pdf';
      document.body.appendChild(element);
      ctx.setState({ loadingApplication: false });
      element.click();
      element.remove();
    })
    .catch((error: Error | string) => {
      console.error(error);
      ctx.setState({ loadingApplication: false });
    });
}

export function downloadPDF(
  ctx: FundOpportunityEOIController,
  draft: boolean = false,
  callback?: () => void,
) {
  ctx.setState({ loadingPDF: true, printCallback: callback });

  ctx
    .getBackendService()
    .generateContentPDF(
      'FundOpportunity',
      ctx.state.fundOpportunity!.id,
      draft,
      `EOI Opportunity ${ctx.state.fundOpportunity!.name}.pdf`,
    )
    .then((result: { jobId: string }) => {
      ctx.setState({
        pdfJobStatus: { jobId: result.jobId, status: 'PENDING', tries: 0 },
      });
    })
    .catch((error: Error | string) => {
      console.error(error);
      ctx.setState({ loadingPDF: false });
    });
}

export function checkPDFJob(ctx: FundOpportunityEOIController) {
  ctx
    .getBackendService()
    .checkPDFJob(ctx.state.pdfJobStatus?.jobId as string)
    .then(
      (jobStatus: { jobId: string; status: 'PENDING' | 'COMPLETE' | 'FAILED'; url: string }) => {
        const currentTries = ctx.state.pdfJobStatus?.tries || 0;
        ctx.setState({
          pdfJobStatus: { ...jobStatus, tries: currentTries + 1 },
        });
      },
    )
    .catch((error: Error | string) => {
      console.error(error);
    });
}

export function initiateUpdateEOI(ctx: FundOpportunityEOIController) {
  ctx.setState({
    eoiIndicativeInvestment: undefined,
    updateEoi: true,
    eoiConfirmed: false,
    editEOI: true,
  });
}

export function initiateWithdrawEOI(ctx: FundOpportunityEOIController) {
  ctx.setState({ withdrawEOI: true });
}

export function cancelUpdateEOI(ctx: FundOpportunityEOIController) {
  ctx.setState({
    updateEoi: false,
    eoiIndicativeInvestment: ctx.state.originalEOIIndicativeInvestment,
    withdrawEOI: false,
    editEOI: false,
  });
}
